/* eslint-disable react/prop-types */
/* eslint-disable import/no-cycle */
/* eslint-disable react/no-array-index-key */
import React from 'react';
import { ContentfulCollection, EntrySkeletonType } from 'contentful';
import camelCase from 'lodash/camelCase';
import {
  ContentfulComponent,
  RenderContentfulComponentParams,
  RenderContentfulPageParams,
} from './types/render-contentful-types';

/**
 * A utility function for rendering a contentful Component UI
 * @param {Object} - { contentfulComponent, entry, options, parents }
 * @description a utility function to render a UI component from Contentful Entry data
 * @example
 * ```javascript
 * const renderContentfulComponent = require('@lululemon/cosy-contentful-renderer')
 * const { baseRenderContentType } = require('@lululemon/ecom-pattern-library-utils-contentful-render-types')
 * const component = renderContentfulComponent({
 *   contentfulComponent: ,
 *   options: {
 *     renderContentType: baseRenderContentType,
 *     callback: () => {} //optional
 *   }
 * })
 * ```
 */

export function renderContentfulComponent({
  contentfulComponent = {},
  entry = {},
  options = {},
  parents = [],
}: RenderContentfulComponentParams = {}): React.ReactElement | null {
  const { sys, __typename, ...componentProperties } = contentfulComponent;

  const callbackOutput =
    options?.callback &&
    (options.callback({
      options,
      contentfulComponent,
      renderContentfulComponent,
    }) as React.ReactElement | null | undefined);

  if (callbackOutput) return callbackOutput as React.ReactElement;

  const fields = componentProperties?.fields ?? componentProperties;
  const contentType = sys?.contentType?.sys?.id || camelCase(__typename);
  if (!contentType) {
    // eslint-disable-next-line no-console
    console.error(`Error rendering Contentful component: no contentType found`);
    return null;
  }

  const renderContentType = {
    ...(options.renderContentType || {}),
  }[contentType];

  if (!renderContentType) {
    // eslint-disable-next-line no-console
    console.error(
      `Error rendering Contentful component: no renderContentType function provided for type ${contentType}`,
    );
    return null;
  }

  const {
    components,
    componentsCollection,
    ...data
  }: {
    components?: ContentfulComponent[];
    componentsCollection?: ContentfulCollection<EntrySkeletonType>;
    [key: string]: unknown;
  } = {
    ...fields,
    entryId: sys?.id,
  };

  // If field is a Reference to nested components, call renderContentfulComponent() on the nested components
  const childComponents = components || componentsCollection?.items;
  let children: unknown[] | null = null;
  if (
    // render children by default, but not if the option is provided to disable
    (options.renderChildren === undefined || options.renderChildren) &&
    Array.isArray(childComponents)
  ) {
    children = childComponents.map((childComponent) =>
      renderContentfulComponent({
        entry,
        contentfulComponent: childComponent,
        options: { ...options, renderContentfulComponent },
        parents: [...parents, contentfulComponent],
      }),
    );
  }

  // Get the output for a content type
  const element = renderContentType({
    children,
    contentfulComponent,
    data,
    entry,
    options: { ...options, renderContentfulComponent },
    parents,
  });

  // If output is null (e.g., filtered out based on a tag) return null
  if (!element) return null;

  const transformedElement = options.transformElement
    ? options.transformElement({
        element,
        contentType,
        contentfulComponent,
        entry,
        parents,
      })
    : element;

  // Wrap in fragment with key added
  return React.cloneElement(transformedElement, { key: sys?.id });
}

/**
 * A utility function for rendering a story page of the contentful Component UI
 * @param {Object} - { entry, options }
 * @description a utility function to render a UI component from Contentful Entry data
 * @example
 * ```javascript
 * const renderContentfulPage = require('@lululemon/cosy-contentful-renderer')
 * const { baseRenderContentType } = require('@lululemon/ecom-pattern-library-utils-contentful-render-types')
 * const component = renderContentfulPage({
 *   entry: contentfulPageEntry,
 *   options: {
 *     renderContentType: baseRenderContentType,
 *   }
 * })
 * ```
 */

export function renderContentfulPage({ entry = {}, options = {} }: RenderContentfulPageParams = {}) {
  if (!entry?.fields?.components?.length) {
    // eslint-disable-next-line no-console
    console.error('Error rendering Contentful entry: no components found');
    return null;
  }
  return entry.fields.components.map((contentfulComponent: ContentfulComponent): React.ReactElement | null =>
    renderContentfulComponent({ entry, contentfulComponent, options }),
  );
}
